import React, { useEffect, useMemo, useRef, useState } from 'react';
import Lottie from 'lottie-react';

const animationCache = new Map();

const ThemedLottie = ({
  src,
  animationData,
  className = '',
  isActive = false,
  idleSpeed = 1,
  activeSpeed = 1.65,
  loop = true,
  autoplay = true,
}) => {
  const lottieRef = useRef(null);
  const [resolvedAnimationData, setResolvedAnimationData] = useState(animationData || null);
  const [isLoading, setIsLoading] = useState(Boolean(src && !animationData));

  const speed = useMemo(
    () => (isActive ? activeSpeed : idleSpeed),
    [isActive, activeSpeed, idleSpeed]
  );

  useEffect(() => {
    if (animationData) {
      setResolvedAnimationData(animationData);
      setIsLoading(false);
      return;
    }

    if (!src) {
      setResolvedAnimationData(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    const cached = animationCache.get(src);

    if (cached) {
      setResolvedAnimationData(cached);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);

    fetch(src, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load Lottie JSON from ${src} (${response.status})`);
        }
        return response.json();
      })
      .then((json) => {
        if (!isMounted) return;
        animationCache.set(src, json);
        setResolvedAnimationData(json);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('ThemedLottie failed to load animation JSON:', error);
        if (isMounted) setResolvedAnimationData(null);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [src, animationData]);

  useEffect(() => {
    if (lottieRef.current?.setSpeed) {
      lottieRef.current.setSpeed(speed);
    }
  }, [speed, resolvedAnimationData]);

  if (isLoading) {
    return (
      <div
        className={`h-full w-full rounded-lg border border-white/10 bg-white/[0.04] ${className}`}
        aria-hidden="true"
      />
    );
  }

  if (!resolvedAnimationData) {
    return (
      <div
        className={`h-full w-full rounded-lg border border-white/10 bg-white/[0.03] ${className}`}
      />
    );
  }

  return (
    <div className={`relative h-full w-full ${className}`}>
      <Lottie
        lottieRef={lottieRef}
        animationData={resolvedAnimationData}
        loop={loop}
        autoplay={autoplay}
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        className="h-full w-full"
      />
    </div>
  );
};

export default ThemedLottie;
